import React, { useEffect, useState } from "react";
import { Printer, Snowflake, AlertTriangle } from "lucide-react";
import "./guide.css";

// Plain-English "Aussie-simple" instructions keyed by manifest step
const INSTRUCTIONS = {
  A01: "Head to the sign-in page. Type your work email and password, then hit Sign in. The four demo buttons on the right fill the form for you.",
  A02:
    "This is home. Five cards up top show what matters right now:\n" +
    "• Today's jobs — how many are on the run sheet for today\n" +
    "• Unassigned — jobs still waiting for a tech\n" +
    "• Overdue services — maintenance agreements past their due date\n" +
    "• Awaiting invoice — completed jobs still to be billed\n" +
    "• Active this week — every job on the books between Monday and Sunday.\n" +
    "Below the cards you get today's run sheet on the left and a quick shortlist of jobs that need someone's eyes on the right.",
  A03: "Click Schedule in the sidebar. Each tech gets a column. Any job that still needs a tech sits in the grey Unassigned bin on the far left.",
  A04: "Hit Week if you want the whole week at a glance instead of just today. The arrows next to the date step you back or forward.",
  A05: "See a job sitting in Unassigned? That's a job with no one on it yet.",
  A06: "Grab it with the mouse and drop it onto a tech's column. They see it on their phone straight away — no messaging needed.",
  A07: "Jobs shows every job on the books, newest first. Click a row to open the full record.",
  A08: "Use the search bar to hunt by job title, client name, site address, or tech.",
  A09: "The status dropdown narrows the list — pick SCHEDULED, EN ROUTE, COMPLETE, etc.",
  A10: "The tech dropdown does the same thing but for people — see just Nelson's list, just Brian's, or anything still unassigned.",
  A11: "Click + New Job (top-right or on the Jobs page). Pick the client, then their site, then the bit of kit (optional). Give the job a plain-English title, set priority, pick a tech if you know who's going, and set when it's on.",
  A12: "Save. You land on the job's own detail page ready to work.",
  A13: "The full record for one job. Client, site, kit, notes, parts, photos, the assigned tech, and status buttons at the top-right that push the job through its lifecycle: SCHEDULED → EN ROUTE → ON SITE → COMPLETE → INVOICED → CLOSED.",
  A14: "Tap Show raw on any note to see exactly what the tech typed on their phone (shorthand and all).",
  A15: "Tap it again and you get the tidied-up version — full words, proper sentences. That's what the office reads.",
  A16: "Log any part used on the job. Every part flows straight into the draft invoice.",
  A17: "Move the job to COMPLETE. A draft invoice card appears in the right column, pre-filled from the parts you logged plus a 1.5 hour labour line at $145/hr.",
  A18: "Every client GT services, alphabetical, one card each.",
  A19: "A client's page: their sites, every bit of kit at each site, and every job GT has ever done for them.",
  A20: "Every invoice in one list. Tabs across the top filter by state.",
  A21: "Draft = the office needs to look at these and approve.",
  A22: "Approve and it moves to the Approved tab — ready to send.",
  A23: "Once it's off to the customer, hit Mark sent.",
  A24: "Money in the bank? Mark paid. The job is closed off automatically.",
  B01: "Same sign-in screen — just on a phone.",
  B02: "Everything on for the tech today. Big cards, big taps.",
  B03: "Tap any card to open the job. You'll see the client, the site, what's needed, and any previous visits to the same bit of kit.",
  B04: "When you leave for site, tap On my way. The office sees you're en route.",
  B05: "When you rock up, tap Arrived. Status changes to ON SITE.",
  B06: "Type what you did — or tap Dictate and just talk. On save it gets tidied for the office (shorthand like 'cr not wrkng' becomes 'Cold room not working').",
  B07: "Log any parts used on site. They flow straight through to the draft invoice.",
  B08: "Done? Tap Mark complete. You bounce back to My Jobs and the office does the rest.",
  C01: "Back in the office, open Invoices → Draft tab. The invoice from the job the tech just completed is sitting there, ready for the owner to approve.",
};

const SECTIONS = [
  {
    id: "office",
    title: "A. Office web (desktop)",
    subtitle: "Everything an office user needs — dashboard, schedule, jobs, clients, invoices.",
    range: (k) => k.startsWith("A"),
  },
  {
    id: "tech",
    title: "B. Tech app (phone)",
    subtitle: "The mobile flow a tech runs on-site — On my way, Arrived, Notes, Parts, Complete.",
    range: (k) => k.startsWith("B"),
  },
  {
    id: "verify",
    title: "C. Verifying the round trip",
    subtitle: "Confirm that a tech's on-site work lands back in the office and produces a draft invoice.",
    range: (k) => k.startsWith("C"),
  },
];

function StepCard({ step, index }) {
  const instr = INSTRUCTIONS[step.key] || step.caption;
  const isTech = step.key.startsWith("B");
  return (
    <article className="guide-step bg-white border border-[#E4E6EF] rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#181854] text-white font-bold flex items-center justify-center text-sm">
          {index}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#4F9CE0]">
            Step {step.key}
          </p>
          <h3 className="text-lg font-semibold text-[#181854] mt-0.5">{step.caption}</h3>
          <p className="mt-2 text-[#14162E] leading-relaxed whitespace-pre-line">
            {instr}
          </p>
        </div>
      </div>

      <div className="mt-5 bg-[#F5F6FA] rounded-xl overflow-hidden border border-[#E4E6EF]">
        <img
          src={step.file}
          alt={step.caption}
          loading="lazy"
          className={`guide-shot w-full h-auto ${isTech ? "max-w-[420px] mx-auto block" : ""}`}
        />
      </div>
    </article>
  );
}

function IssueRow({ item, i }) {
  return (
    <div className="guide-step bg-white border border-[#E4E6EF] rounded-2xl p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] text-[#92400E] flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#6A6E85]">
            Issue #{i + 1} · <span className="text-[#181854]">{item.file}</span>
          </p>
          <p className="mt-1.5 text-[#14162E] leading-relaxed">
            <strong>Friction:</strong> {item.issue}
          </p>
          <p className="mt-1.5 text-[#181854] leading-relaxed">
            <strong>Suggested fix:</strong> {item.suggested_fix}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Guide() {
  const [manifest, setManifest] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch("/howto/manifest.json").then((r) => r.json()),
      fetch("/howto/issues.json").then((r) => r.json()),
    ])
      .then(([m, is]) => {
        setManifest(m);
        setIssues(is);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F6FA] text-[#14162E]">
      {/* Header */}
      <header className="bg-[#181854] text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
              <img
                src="/brand/gt-logo.webp"
                alt="GT Refrigeration & Aircon Services"
                className="h-10 w-auto object-contain"
                style={{ maxWidth: "220px" }}
              />
            </div>
            <div className="hidden sm:block">
              <p className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                <Snowflake size={11} strokeWidth={2.4} /> GT Field OS
              </p>
              <h1 className="text-lg font-semibold">How-to guide</h1>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="guide-print-btn"
            className="no-print inline-flex items-center gap-2 bg-white text-[#181854] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#ECEDF6] transition-colors duration-200"
          >
            <Printer size={16} /> Print
          </button>
        </div>
      </header>

      {/* Intro */}
      <section className="max-w-5xl mx-auto px-6 pt-10 pb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#4F9CE0] font-semibold">
          Commercial Cooling. Performance Proven.
        </p>
        <h2 className="text-4xl font-bold text-[#181854] tracking-tight mt-2">
          Running a job in GT Field OS
        </h2>
        <p className="mt-3 text-[#6A6E85] max-w-2xl leading-relaxed">
          A screenshot-by-screenshot walkthrough — from taking the booking to marking
          the invoice paid — captured live from the running app. Written for
          non-technical office staff and techs in the field. Use the Print button top-right
          for a paper copy.
        </p>
      </section>

      {loading && (
        <div className="max-w-5xl mx-auto px-6 py-10 text-[#6A6E85]">Loading…</div>
      )}

      {/* Sections */}
      {!loading &&
        SECTIONS.map((section) => {
          const steps = manifest.filter((s) => section.range(s.key));
          if (steps.length === 0) return null;
          return (
            <section
              key={section.id}
              className="guide-section max-w-5xl mx-auto px-6 py-8"
              data-testid={`guide-section-${section.id}`}
            >
              <div className="border-t border-[#E4E6EF] pt-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#181854] tracking-tight">
                  {section.title}
                </h2>
                <p className="text-[#6A6E85] mt-1">{section.subtitle}</p>
              </div>

              <div className="mt-6 space-y-6">
                {steps.map((step, idx) => (
                  <StepCard key={step.key} step={step} index={idx + 1} />
                ))}
              </div>
            </section>
          );
        })}

      {/* Extra nav note */}
      {!loading && (
        <section className="max-w-5xl mx-auto px-6 py-8">
          <div className="border-t border-[#E4E6EF] pt-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#181854] tracking-tight">
              D. Anything else in the app
            </h2>
            <p className="text-[#6A6E85] mt-1">
              Everything currently in the sidebar is covered above — Dashboard, Schedule,
              Jobs, Clients &amp; Assets, Invoices. Two related features live behind the
              scenes (no dedicated screen yet):
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-[#E4E6EF] rounded-2xl p-5 shadow-sm">
                <h3 className="font-semibold text-[#181854]">Maintenance agreements</h3>
                <p className="text-sm text-[#14162E] mt-1 leading-relaxed">
                  Every client / asset that is on a recurring service is tracked in the
                  background. When one falls due, running the maintenance sweep creates
                  a NEW job automatically and rolls the next due date forward. The
                  &quot;Overdue services&quot; KPI on the dashboard is your heads-up.
                </p>
              </div>
              <div className="bg-white border border-[#E4E6EF] rounded-2xl p-5 shadow-sm">
                <h3 className="font-semibold text-[#181854]">Auto-tidy notes</h3>
                <p className="text-sm text-[#14162E] mt-1 leading-relaxed">
                  Every note a tech saves gets rewritten from shorthand into
                  office-friendly English (e.g. &quot;cr not wrkng, chkd compr&quot; →
                  &quot;Cold room not working. Checked compressor.&quot;). Toggle
                  &quot;Show raw&quot; on any note to see what the tech actually typed.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Issues & Friction Log */}
      {!loading && issues.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-8">
          <div className="border-t border-[#E4E6EF] pt-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#181854] tracking-tight">
                  Issues &amp; friction log
                </h2>
                <p className="text-[#6A6E85] mt-1">
                  Every dead-end, confusing label or awkward step we hit during this
                  walkthrough — documented, not fixed. Decide which ones to sort next.
                </p>
              </div>
              <span className="bg-[#FEF3C7] text-[#92400E] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                {issues.length} item{issues.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="mt-6 space-y-3">
              {issues.map((it, i) => (
                <IssueRow key={i} item={it} i={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="max-w-5xl mx-auto px-6 py-10 text-center text-xs text-[#6A6E85]">
        © {new Date().getFullYear()} GT Refrigeration &amp; Aircon Services · GT Field OS
      </footer>
    </div>
  );
}
